import { Badge, Box, Button, Grid, Group, Text } from '@optiaxiom/react';
import { IconLink, IconLinkSlash } from '@optimizely/axiom-icons';

import { ProviderConnectionSummary } from '@frontend/types';

type ProviderGridItemProps = {
  provider: ProviderConnectionSummary;
  onConnect: (providerName: string, scopeBundleId: string) => void;
  onDisconnect: (credentialId: string) => void;
  isConnecting: boolean;
};

const getStatusBadge = (status: ProviderConnectionSummary['connectionStatus']) => {
  switch (status) {
    case 'connected':
      return <Badge intent="success">Connected</Badge>;
    case 'expired':
      return <Badge intent="warning">Expired</Badge>;
    case 'not_connected':
    default:
      return <Badge intent="neutral">Not Connected</Badge>;
  }
};

export function ProviderGridItem({
  provider,
  onConnect,
  onDisconnect,
  isConnecting,
}: ProviderGridItemProps) {
  const handleConnect = () => {
    onConnect(provider.providerName, provider.scopeBundleId);
  };

  const handleDisconnect = () => {
    if (provider.connection?.id) {
      onDisconnect(provider.connection.id);
    }
  };

  return (
    <Box
      border="1"
      borderColor="border.secondary"
      rounded="md"
      p="16"
      bg="bg.default"
      display="flex"
      flexDirection="column"
      gap="12"
    >
      <Group flexDirection="row" justifyContent="space-between" alignItems="start" gap="8">
        <Box display="flex" flexDirection="column" gap="2">
          <Text fontWeight="600">{provider.providerName}</Text>
          <Text fontSize="sm" color="fg.tertiary">
            {provider.scopeBundleName}
          </Text>
        </Box>
        {getStatusBadge(provider.connectionStatus)}
      </Group>

      {provider.scopeBundleDescription && (
        <Text fontSize="sm" color="fg.secondary">
          {provider.scopeBundleDescription}
        </Text>
      )}

      {provider.connectionStatus === 'expired' && (
        <Text fontSize="sm" color="fg.tertiary">
          Connection will auto-refresh when used
        </Text>
      )}

      <Box mt="auto">
        {provider.connectionStatus === 'not_connected' && (
          <Button appearance="primary" size="sm" onClick={handleConnect} disabled={isConnecting}>
            <IconLink size={16} />
            {isConnecting ? 'Connecting...' : 'Connect'}
          </Button>
        )}
        {provider.connectionStatus === 'expired' && (
          <Button appearance="primary" size="sm" onClick={handleConnect} disabled={isConnecting}>
            <IconLink size={16} />
            {isConnecting ? 'Reconnecting...' : 'Reconnect'}
          </Button>
        )}
        {provider.connectionStatus === 'connected' && provider.connection?.id && (
          <Button appearance="subtle" size="sm" onClick={handleDisconnect}>
            <IconLinkSlash size={16} />
            Disconnect
          </Button>
        )}
      </Box>
    </Box>
  );
}

type ProviderConnectionGridProps = {
  providers: ProviderConnectionSummary[];
  onConnect: (providerName: string, scopeBundleId: string) => void;
  onDisconnect: (credentialId: string) => void;
  isConnecting: boolean;
};

export function ProviderConnectionGrid({
  providers,
  onConnect,
  onDisconnect,
  isConnecting,
}: ProviderConnectionGridProps) {
  return (
    <Grid gridTemplateColumns={['1', '2', '3']} gap="12" w="full">
      {providers.map(provider => (
        <ProviderGridItem
          key={`${provider.providerId}-${provider.scopeBundleId}`}
          provider={provider}
          onConnect={onConnect}
          onDisconnect={onDisconnect}
          isConnecting={isConnecting}
        />
      ))}
    </Grid>
  );
}
